/**
 * 应用/重置逻辑
 * 1. tab 切换不影响内容
 * 2. 【筛选】和【精确搜索】互斥，点击【应用】提交当前数据，另一个数据清空，【视图】保存
 * 3. 点击 【重置】清空当前的已输入/已选中，停留在当前页面不调接口
 * 4. 在【视图】页面点击【重置】时，重置到默认视图样式，停留在当前页面不调接口
 */
import React, { ChangeEvent, PureComponent } from 'react';
import { Button, Collapse, Icon, Input, Layout, Radio } from 'antd';
import { RangePickerValue } from 'antd/lib/date-picker/interface';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { RadioChangeEvent } from 'antd/lib/radio';
import { isEqual } from 'lodash';
import moment from 'moment';

import {
  IReportConfigProps,
  IReportQueryProps,
  IReportQueryViewProps,
  IReportViewProps,
} from '@duoke/tools/umd/report';
import { createPrefixClassnames } from 'src/tools';
import { DokDatePicker } from 'src/components';
import CheckGroup from './CheckGroup';
import RadioGroup from './RadioGroup';
import View from './View';
import styles from './index.less';
import CheckboxGroup from 'antd/es/checkbox/Group';

const dok = createPrefixClassnames(styles, 'sider-filter');
const { Sider } = Layout;
const { Panel } = Collapse;

//用于表示多精度字段
export interface IReportQueryPropsForMultiplePrecisions {
  precisions?: CheckboxValueType[];
}

const defaultProps = {
  config: {},
  values: {},
};
const initialState = {
  values: {} as IReportQueryProps & IReportQueryPropsForMultiplePrecisions,
  active: 'filter', // 当前项
  activeKey: [] as string[], // 折叠面板展开项
};

interface IProps {
  config: IReportConfigProps;
  values: IReportQueryProps;
  collapsed: boolean;
  onCollapse?: () => void;
  onChange?: (v: IReportQueryProps) => void;
}

type State = Readonly<typeof initialState>;

export default class FilterSider extends PureComponent<IProps, State> {
  static defaultProps = defaultProps;
  readonly state = initialState;

  get tabOption() {
    const {
      config: { filters, preciseSearch },
    } = this.props;

    return [
      filters && !!filters.length && { value: 'filter', label: '筛选' },
      preciseSearch && !!preciseSearch.length && { value: 'search', label: '精确搜索' },
      { value: 'view', label: '视图' },
    ].filter((v) => v && v.value) as Array<{ value: string; label: string }>;
  }

  get viewSource() {
    const {
      config: { views, info },
    } = this.props;
    const { values } = this.state;
    //todo 与 utils.ts 代码重复
    const precisions = (info.styleKey === '1' ? [values.precision] : values.precisions) ?? [];
    const source = views.filter(
      (v) =>
        (!v.precisionKeys) || v.precisionKeys.length === 0 || (v.precisionKeys.some(key => precisions.includes(key))),
    );
    return source.reduce((acc, v) => ((acc[v.value] = v), acc), {} as Record<string,
      IReportViewProps>);
  }

  componentDidMount() {
    const {
      config: { filters, preciseSearch },
      values,
    } = this.props;

    const activeKey = [...filters, ...preciseSearch].map((v) => v.value).filter((v) => !!v);

    this.setState({
      values,
      active: (this.tabOption[0] || {}).value,
      activeKey: [...(activeKey || []), 'precision', 'view'],
    });
  }

  componentDidUpdate(prevProps: IProps) {
    const {
      config,
      config: { filters, preciseSearch },
      values,
    } = this.props;

    const activeKey = [
      ...[...filters, ...preciseSearch].map((v) => v.value).filter((v) => !!v),
      'precision',
      'view',
    ];

    if (!isEqual(prevProps.values, values) && !isEqual(values, this.state.values)) {
      this.setState({ values: { ...values } });
    }

    if (!isEqual(config, prevProps.config) && !isEqual(activeKey, this.state.activeKey)) {
      this.setState({ activeKey: [...activeKey] });
    }
    const active = (this.tabOption[0] || {}).value;
    if (
      this.state.active !== active &&
      !this.tabOption.find((v) => v.value === this.state.active)
    ) {
      this.setState({ active });
    }
  }

  render() {
    const {
      collapsed,
      onCollapse,
      config: { info, filters, preciseSearch, precisions, views },
    } = this.props;
    const { values, active, activeKey } = this.state;
    return (
      <Sider
        width={255}
        theme="light"
        collapsed={collapsed}
        collapsedWidth={0}
        className={dok()}
        style={{ marginRight: collapsed ? 0 : 15 }}
      >
        <div className={dok('operation')}>
          <div className={dok('operation-btn')}>
            <Button type="primary" onClick={this.handleApply}>
              应用
            </Button>
            <Button onClick={this.handleReset}>重置</Button>
          </div>
          <div>
            <Icon type="left-square" onClick={onCollapse} className={dok('collapse-icon')}/>
          </div>
        </div>
        {this.tabOption && this.tabOption.length > 1 && (
          <div className={dok('tabs')}>
            <Radio.Group
              size="small"
              value={active}
              onChange={this.handleSiderChange}
              className={dok('tabs-radio')}
            >
              {this.tabOption.map((v) => (
                <Radio.Button key={v.value} value={v.value}>
                  {v.label}
                </Radio.Button>
              ))}
            </Radio.Group>
          </div>
        )}
        <div className={dok('collapse')}>
          {active === 'filter' && info && info.preFilter && (
            <div className={dok('preview-filter')}>
              {info.preFilter.split('\n').map((v: string) => (
                <div key={v}>{v}</div>
              ))}
            </div>
          )}
          <Collapse
            bordered={false}
            activeKey={activeKey}
            style={{ width: '100%' }}
            expandIconPosition="right"
            onChange={this.handleCollapseChange}
          >
            {active === 'filter' &&
            filters &&
            filters.map((v, i) => (
              <Panel header={`${v.name}:`} key={v.value || i}>
                <CheckGroup
                  dataSource={
                    v.hasEmpty
                      ? [{ name: `-${v.name}为空-`, value: -1 }, ...(v.options || [])]
                      : v.options || []
                  }
                  onChange={this.handleFilterChange(v.value)}
                  value={((values.filters || {})[v.value] || {}).val}
                />
              </Panel>
            ))}
            {active === 'search' &&
            preciseSearch &&
            preciseSearch.map((v) => (
              <Panel header={`${v.name}:`} key={v.value}>
                {v.type === 'text' ? (
                  <Input
                    allowClear
                    size="small"
                    onChange={this.handleInputChange(v.value)}
                    value={((values.filters || {})[v.value] || {}).val}
                  />
                ) : (
                  <DokDatePicker
                    gutter={1}
                    mode="vertical"
                    onChange={this.handleDateChange(v.value)}
                    defaultValue={this.getDefaultDate((values.filters || {})[v.value])}
                  />
                )}
              </Panel>
            ))}
            {active === 'view' &&
            precisions &&
            precisions.map((v) => {
              switch (info.styleKey) {
                case '1':
                  return (
                    <Panel header={'精度:'} key="precision">
                      <RadioGroup
                        simple
                        dataSource={v.options || []}
                        value={values.precision || ''}
                        onChange={this.handlePrecisionChange}
                      />
                    </Panel>
                  );
                case '11':
                  return (null);
                case '12':
                  return (
                    <Panel header={'精度:'} key="precision">
                      <CheckboxGroup
                        value={(values?.precisions?.length ?? 0) > 0 ? values.precisions : (v.options ?? [])
                          .filter((o) => o.isSelected).map((o) => o.value)}
                        options={(v.options ?? []).map((o) => ({
                          label: o.name,
                          value: o.value,
                        }))}
                        onChange={this.handleMultiplePrecisionsChange}
                      />
                    </Panel>
                  );
              }

            })}
            {active === 'view' && views && (
              <Panel header="视图" key="view">
                <View
                  disableConfig={[]}
                  dataSource={this.viewSource}
                  onChange={this.handleViewChange}
                  defaultValue={values.views || []}
                />
              </Panel>
            )}
          </Collapse>
        </div>
      </Sider>
    );
  }

  // 获取日期选择器的默认值
  getDefaultDate = (filter?: { val: string[]; action: string }) => {
    if (!filter || !filter.val || !filter.val.length) {
      return undefined;
    }

    const date = filter.val;
    return [
      date[0] ? moment(date[0]) : undefined,
      date[1] ? moment(date[1]) : undefined,
    ] as RangePickerValue;
  };
  // 应用
  handleApply = () => {
    const { onChange } = this.props;
    const { values } = this.state;
    onChange && onChange(values);
  };
  // 重置
  handleReset = () => {
    const { values } = this.props;
    const {
      values: { filters, ...props },
      active,
    } = this.state;

    let val;
    if (active === 'view') {
      val = { ...values, filters };
    } else {
      val = {
        ...props,
        filters: Object.keys(filters).reduce(
          (acc, k) => {
            if (
              active === 'filter' &&
              typeof filters[k] === 'object' &&
              filters[k]?.action !== 'IN'
            ) {
              acc[k] = filters[k];
            }
            if (
              active === 'search' &&
              (typeof filters[k] === 'string' || filters[k].action === 'IN')
            ) {
              acc[k] = filters[k];
            }
            return acc;
          },
          {} as Record<string, any>,
        ),
      };
    }

    this.setState({
      values: { ...val },
    });
  };
  // 切换 Tabs
  handleSiderChange = (e: RadioChangeEvent) => {
    this.setState({ active: e.target.value });
  };
  // 折叠面板 onChange
  handleCollapseChange = (key: string | string[]) => {
    this.setState({ activeKey: [...key] });
  };
  // 多选(筛选) onChange
  handleFilterChange = (key: string) => {
    return (val: CheckboxValueType[]) => {
      const { values } = this.state;

      this.setState({
        values: { ...values, filters: { ...(values.filters || {}), [key]: { val, action: 'IN' } } },
      });
    };
  };
  // 文本框(精确筛选) onChange
  handleInputChange = (key: string) => {
    return (e: ChangeEvent<HTMLInputElement>) => {
      const { values } = this.state;

      const val = (e.target.value || '').replace(/(^\s*)|(\s*$)/g, '');
      this.setState({
        values: {
          ...values,
          filters: { ...(values.filters || {}), [key]: { val, action: 'FUZZY_LIKE' } },
        },
      });
    };
  };
  // 日期选择器 onChange
  handleDateChange = (key: string) => {
    return (
      date: RangePickerValue,
      dateString: string[],
      k?: string | number | undefined,
      tip?: string | undefined,
    ) => {
      const { values } = this.state;

      this.setState({
        values: {
          ...values,
          filters: {
            ...(values.filters || {}),
            [key]: { val: [dateString[0], dateString[1]], action: 'BETWEEN' },
          },
        },
      });
    };
  };
  // 单选(精度) onChange
  handlePrecisionChange = (val: CheckboxValueType) => {
    const { values } = this.state;
    const precision = String(val || '');

    this.setState({ values: { ...values, precision } });
  };
  handleMultiplePrecisionsChange = (val: Array<CheckboxValueType>) => {
    if (val.length === 0) {
      return;
    }
    const { values } = this.state;
    this.setState({ values: { ...values, precisions: [...val] } });
  };
  // 视图 onChange
  handleViewChange = (val: IReportQueryViewProps[]) => {
    const { values } = this.state;
    this.setState({
      values: {
        ...values,
        views: [...val],
      },
    });
  };
}
